/**
 * ecritureDeForme — le socle commun des écritures de forme : ce que
 * `format_text` (#1863, #1865), `format_paragraphs` (#1866, #1867) et
 * `copy_format` (#1869) font de la même façon, quel que soit le réglage
 * (spec #1858, règles communes).
 *
 * WHY un socle : les exécuteurs ne gardent que ce qui leur est propre — QUOI
 * écrire (`planDeFormatText.ts`, `reglagesDeParagraphe.ts`, `remiseAZero.ts`,
 * `copieDeForme.ts`) et COMMENT juger
 * (`recuDEcritureDeForme.ts`). Tout ce qui lie une écriture à l'état du
 * document — révision, fichier exporté, clôture — est ici, une fois.
 *
 * ## Les règles communes
 *
 * - **Écriture directe.** Tout part en `changeMode:'direct'`, quel que soit le
 *   régime de suivi : la forme ne crée jamais de révision. Mesuré sous suivi
 *   actif (`mesure-forme-directe-sous-suivi`, `mesure-format-text-cibles`,
 *   `mesure-format-paragraphs` A1 à E9 ; SuperDoc 2.15.1, 2026-10-02) : 0
 *   révision, aucun `w:rPrChange` ni `w:pPrChange` au fichier.
 * - **Deux exports par appel, jamais un par paragraphe.** Le premier résout la
 *   cible (`cibleDeParagraphes.ts` : la seule résolution de cible des outils de
 *   forme), le second relit ce qui a été écrit. L'accusé de l'éditeur n'est
 *   jamais le juge.
 * - **⚠️ Pas de garde de portée d'inspection, et c'est voulu.** Les verbes de
 *   CONTENU refusent une cible que la dernière lecture n'a pas publiée
 *   (`inspectedPublicNodeIds`). Une écriture de forme ne la consulte pas : elle
 *   ne change aucun texte, ses identifiants sont confrontés au fichier de
 *   l'acte (ceux qu'il n'y trouve pas sont nommés), et la révision attendue
 *   garde l'écriture contre un document qui a bougé. Sans cela, « tout l'acte »
 *   serait refusé au-delà des 250 repères d'une lecture globale, et un
 *   identifiant lu par `canvas_inspect` `formatOf` ne pourrait pas être écrit.
 *
 * - **Deux manières d'écrire, choisies une fois.** {@link ManiereDEcrire} :
 *   par plans atomiques dans le corps ({@link ecritureParPlans}, tout ce que
 *   cet en-tête dit des plans) ; directe dans un en-tête, un pied ou une note,
 *   où l'éditeur refuse les plans (`ecritureDirecteDansUnLieu`). Le lieu de la
 *   cible décide, à l'ouverture, et rien d'autre ne le reteste : l'acte rendu
 *   est celui du lieu, `apercevoir` et `appliquerLesPlans` gardent leur
 *   contrat. Pourquoi, et ce que la manière directe coûte : en-tête de
 *   `horsDuCorps.ts`, seul endroit qui le porte.
 *
 * ## Le déroulé, toujours le même
 *
 * 1. {@link ouvrirLEcritureDeForme} : instantané, surfaces de l'éditeur,
 *    révision, puis le fichier Word exporté et lu, clôturé sur la révision. La
 *    cible se résout sur CE fichier (`cibleDeParagraphes.ts`), dont le corps est
 *    rendu avec ses paragraphes de cellule de tableau à leur place
 *    (`corpsAvecSesCellules`, `lecteurDeForme.ts`) : l'éditeur les écrit comme
 *    les autres, par plans et hors plan (§16 de `superdoc-document-api.md`).
 * 2. `apercevoir` : tout plan est soumis à l'aperçu avant la première écriture ;
 *    les étapes que l'éditeur refuserait sont connues document intact, et
 *    retirées du plan.
 * 3. `appliquerLesPlans`, `ecrireUnParagraphe` : les deux manières d'écrire.
 * 4. `clore` : le fichier exporté APRÈS l'écriture est le seul juge, et
 *    seulement s'il est bien celui de l'écriture.
 *
 * ## Un plan de texte avant la forme (`rewrite_block`)
 *
 * Un seul verbe écrit du TEXTE par le socle : `rewrite_block`
 * (`rewriteBlockExecutor.ts`), qui réécrit des paragraphes puis repose la forme
 * de leur texte. Son plan de texte (une étape `text.rewrite` par paragraphe)
 * passe par les mêmes aperçu, application et repli que les plans de forme, et
 * part le PREMIER : la révision qu'il rend est celle que le plan de forme
 * attend. Il suit le régime du document (`OptionsDesPlans.changeMode`) ; les
 * plans de forme qui le suivent restent directs. Mesures : §17 de
 * `superdoc-document-api.md`.
 *
 * ## Ce que la révision garde
 *
 * - `mutations.apply` rend la révision du document après son écriture
 *   (`revision.after`), dans l'espace de `doc.info().revision` ; un plan de
 *   deux étapes la fait avancer de 3 (`mesure-format-text-cibles`, U1,
 *   2026-10-02). Chaque plan attend la révision que le précédent a RENDUE : une
 *   frappe de l'avocat entre deux plans fait refuser le suivant
 *   (`REVISION_MISMATCH`, jeté : rien n'est écrit) au lieu d'être écrite
 *   par-dessus. Un plan appliqué sans révision rendue laisse le suivant sans
 *   verrou : il n'est pas soumis.
 * - Pendant un export, `mutations.apply` REND le refus passager (il ne le jette
 *   pas) : la fermeture passée à `muterSousFences` porte ce seul appel, et le
 *   réessai de `refusPassager.ts` s'y applique.
 * - Une écriture par paragraphe REND son refus, elle ne le jette pas
 *   (`mesure-format-paragraphs`, 2026-10-02 : `NO_OP` en R2 et E2,
 *   `REVISION_MISMATCH` sur une révision périmée en Q4b), et ne rend PAS la
 *   révision qu'elle laisse : la révision est relue avant chacune, puis une
 *   fois après la dernière. Deux fenêtres restent donc ouvertes, que rien ne
 *   permet de fermer : une frappe ENTRE deux écritures de la série (la suivante
 *   relit la révision et l'accepte — elle vise un paragraphe par son
 *   identifiant, pas un texte), et une frappe entre la dernière écriture et la
 *   lecture de sa révision.
 * - La relecture est clôturée sur la révision que la dernière écriture a
 *   laissée, pas sur un instantané pris ensuite : une frappe de l'avocat après
 *   l'écriture serait sinon jugée comme l'écriture. Après une écriture REFUSÉE,
 *   cette révision ne vaut plus — un refus `NO_OP` peut faire avancer la
 *   révision sans rien écrire (en-tête de `formatTextExecutor.ts`) : la clôture
 *   retombe sur l'instantané d'après, et le reçu d'une écriture interrompue ne
 *   dit de toute façon aucun réglage conforme. Sans révision à quoi se clore,
 *   le fichier ne juge rien (`revision_unavailable`). Sans écriture tentée, le
 *   fichier lu à l'ouverture est déjà cette relecture.
 *
 * ## Ce qui compte comme une écriture
 *
 * `ecritures` compte les entrées de l'historique de l'éditeur : c'est ce que
 * `undo_changes` défait. Un plan qui ne change rien est accepté sans entrée
 * d'historique et rend la révision qu'il a reçue (`mesure-format-paragraphs`,
 * A2 : `revision.before` = `revision.after`) : il n'est pas compté — c'est le
 * seul signal, l'effet annoncé par étape (`effect:"changed"`) ne le dit pas. Une écriture par paragraphe refusée `NO_OP` (R2,
 * E2) n'est pas un échec : le paragraphe est dans l'état demandé, elle est
 * tenue pour sans objet et n'est pas comptée.
 *
 * `ecritures` ne compte que les écritures de l'AGENT. La profondeur de
 * l'historique est lue à l'ouverture et à la clôture (`history.get`, 0,03 ms) :
 * si elle n'a pas gagné ce que ces écritures expliquent — une entrée chacune,
 * jusqu'à la profondeur que l'éditeur garde —, l'avocat a tapé, ou une autre
 * écriture est passée, pendant l'appel. Annuler « autant de fois que le reçu
 * compte d'écritures » déferait alors SA frappe : le reçu le dit
 * (`historiqueInexplique` → `format.writesNotUndoable` vaut `format.writes`, et
 * l'effet l'explique). Sans profondeur lisible, rien n'est dit.
 *
 * ## Quand l'éditeur refuse un lot (#1868)
 *
 * Un plan est atomique : une étape refusée fait refuser tout le lot. L'écriture
 * est alors posée sur ce que l'éditeur accepte, et ce qu'il refuse est SAUTÉ et
 * nommé au reçu, avec la raison (`format.skipped`, ou
 * `format.onlyWhere.passagesSetAside` sous un filtre). Les décisions sont
 * pures et vivent dans `repliDeLot.ts` ; ce qui suit est ce que l'éditeur fait,
 * relevé par `tests/ws-mocked/mesure-repli-de-lot.spec.ts` (SuperDoc 2.15.1,
 * 2026-10-02, rejoué le 2026-10-04, éditeur en mode suggestion, appels en direct).
 *
 * - **L'aperçu nomme toutes les étapes qu'il refuse, en une passe** (L1 ;
 *   `mesure-format-text-only-where`, Q16). Elles sont retirées et le reste est
 *   aperçu de nouveau, puis appliqué en UN plan : une seule annulation.
 *   Refusés à l'aperçu : une étape de bloc sur un paragraphe sans texte
 *   (`INVALID_TARGET`, L1) ; une étape par texte derrière un appel de note, ou
 *   dans un paragraphe dont du texte est déplacé en attente
 *   (`REVISION_MISMATCH`, L6, L13).
 * - **⚠️ `REVISION_MISMATCH` a deux sens à l'aperçu** : le document a changé
 *   (« expected-revision 5 does not match current revision 6. », L5), ou
 *   l'éditeur ne sait pas situer un passage (« Re-run query.match()… », L13,
 *   révision inchangée). Seule la révision RELUE les distingue : si elle a
 *   bougé, l'appel est refusé (`STALE_CANVAS_REVISION`) ; sinon c'est l'étape
 *   qui est refusée.
 * - **L'aperçu ne voit pas tout.** Ces étapes passent l'aperçu et font JETER
 *   l'application, sans que l'erreur nomme l'étape (`stepId` absent, L3, L7) :
 *   une étape de bloc sur un paragraphe où un signet couvre une part du texte,
 *   où passe un signet à cheval, ou qui reçoit un texte déplacé en attente
 *   (`INVALID_CONTEXT`, « straddles a bookmark, protection, or move-range
 *   marker ») ; toute étape qui touche un texte dont la forme a changé sous
 *   suivi (`INVALID_CONTEXT`, « unsupported-context », L6, L9) ; une étape de
 *   bloc qui croise un contrôle de contenu verrouillé (`CAPABILITY_UNAVAILABLE`,
 *   L6) ; une étape de bloc sur un paragraphe où une plage de permission couvre
 *   une part du texte (`INVALID_CONTEXT`, même message que le signet, L6) ; une
 *   étape sans effet (`NO_OP`, L4).
 * - **Un plan refusé à l'application n'écrit rien** (fichier identique, aucune
 *   entrée d'historique, L3, L4, L7), **mais fait avancer la révision** du
 *   nombre d'étapes jouées avant le refus, plus une (L11 : 0, +2, +6, +8 pour
 *   une étape refusée au rang 0, 1, 5, 7). La révision est donc RELUE après
 *   chaque refus, et son avance est jugée (`avanceApresUnRefus`,
 *   `repliDeLot.ts`) : nulle, ou de 2 au nombre d'étapes du plan ; toute autre
 *   avance interrompt la série (`STALE_CANVAS_REVISION`). ⚠️ La fenêtre n'est
 *   pas fermée : une frappe de l'avocat pendant un plan refusé à un rang bas
 *   reste sous la borne. Les étapes visent un paragraphe par son identifiant ;
 *   celles qui visent un passage par sa POSITION sont re-prouvées après chaque
 *   refus qui a joué une étape, sur le texte entier du paragraphe
 *   (`OptionsDesPlans.reprouver`).
 * - **Un signet ne gêne que s'il s'ouvre ou se ferme au milieu du texte** (L6 :
 *   refusés — signet sur une part du texte, signet vide au milieu, signet parti
 *   du début et refermé au milieu, signet à cheval ; acceptés — signet vide en
 *   fin de paragraphe, signet autour de tout le texte). Dans un tel paragraphe,
 *   un passage est écrit ou refusé selon sa place : devant le signet, sur lui
 *   ou en lui, il est écrit ; juste après lui ou à cheval sur son début, il est
 *   refusé (L8). **Un contrôle de contenu ne gêne que si son CONTENU est
 *   verrouillé** (`sdtContentLocked`, `contentLocked`) ; verrouillé dans son
 *   seul cadre (`sdtLocked`) ou libre, il est écrit (L6). Un lien, un champ, un
 *   commentaire, un appel de note ne font pas refuser une étape de bloc (L6).
 * - Le lot refusé est rejoué **par moitiés** jusqu'à l'étape en cause : le
 *   refus ne la nommant pas, c'est le chemin qui laisse le moins d'entrées
 *   d'historique (une par plan appliqué — le reçu les compte, `format.writes`).
 *   Une étape refusée SEULE est sautée ; sans effet, elle est tenue pour sans
 *   objet. Un plan d'une étape coûte 5 à 10 ms sur un petit acte (L12).
 * - **Rien n'est rejoué** quand le refus porte sur le document — révision
 *   périmée, éditeur en lecture seule — ou n'a pas de code : la série est
 *   interrompue, comme avant.
 * - **Aucune écriture acceptée ne modifie une révision en attente**, à une
 *   exception près. Insertion, suppression, déplacement, révision à cheval sur
 *   une marque de paragraphe : `w:ins`, `w:del`, `w:moveFrom`, `w:moveTo`,
 *   `w:rPrChange`, `w:pPrChange` gardent auteur, date, étendue et forme d'avant,
 *   et `trackChanges.list` rend les mêmes entrées (L6, 33 paragraphes × 6
 *   écritures). L'exception : une forme de paragraphe posée sur un paragraphe
 *   qui porte un `w:pPrChange` est acceptée et change ce que la révision
 *   propose — voir « Un paragraphe dont la forme attend une décision ».
 * - Non mesuré : un changement de forme en attente porté par la marque de
 *   paragraphe (`w:pPr/w:rPr/w:rPrChange`) ; une plage de permission autour de
 *   tout le texte d'un paragraphe.
 *
 * Une écriture par paragraphe (`ecrireUnParagraphe`) refusée interrompt
 * toujours la série : elle n'a pas de lot à rejouer.
 *
 * Le repli vaut pour tout plan du socle : les étapes d'alignement de
 * `format_paragraphs`, les étapes de texte de sa remise à zéro (`reset`) et
 * les étapes de texte et d'alignement de `copy_format`. Un paragraphe dont
 * l'étape est refusée est sauté pour tout l'appel.
 *
 * ### Un paragraphe dont la forme attend une décision (`w:pPrChange`)
 *
 * Une seule règle pour tous les verbes qui écrivent une forme de paragraphe,
 * portée par {@link ecrireOuEpargnerLesRevisionsDeFormeDeParagraphe} : le
 * paragraphe est ÉCRIT, et le reçu le nomme
 * (`format.writtenOverPendingParagraphRevision`,
 * `FORMAT_WRITTEN_OVER_PENDING_REVISION`). ⚠️ Décision de l'orchestrateur du
 * chantier (2026-10-04), d'après l'usage décrit par le porteur — les avocats
 * laissent les révisions en attente et acceptent tout à la fin —, **à
 * confirmer par le porteur**.
 *
 * Ce que l'éditeur fait alors (`mesure-revision-de-forme-de-paragraphe`,
 * `mesure-repli-de-lot` L6, `mesure-copy-format` M1 à M4, SuperDoc 2.15.1,
 * 2026-10-04) : l'écriture est acceptée ; le `w:pPrChange` garde son
 * identifiant, son auteur, sa date et la forme d'AVANT ; ce que la révision
 * PROPOSE inclut désormais la forme posée (`trackChanges.list` :
 * « alignment: ∅ → center » devient « ∅ → right, indentLeft: ∅ → 720 »).
 * Accepter la révision garde la forme de l'agent ; la rejeter ramène le
 * paragraphe à la forme d'avant, et la forme de l'agent part avec la
 * proposition du confrère. Un alignement écrit par-dessus un alignement
 * proposé le remplace : l'avocat ne peut plus accepter celui du confrère.
 *
 * Ce n'est PAS le cas d'une révision que l'écriture EFFACERAIT : une remise à
 * zéro sur un `w:pPrChange` ou sur une marque de paragraphe insérée ou
 * supprimée (`remiseAZero.ts`). Ces paragraphes-là sont laissés d'avance et
 * nommés (`format.skipped`, `pending_tracked_change`).
 *
 * ### ⚠️ La page après un plan refusé
 *
 * Défaut de l'ÉDITEUR, suivi par
 * https://github.com/hector-ai-legal/hector-monorepo/issues/1913 et mesuré par
 * `tests/ws-mocked/mesure-gel-de-peinture.spec.ts` (SuperDoc 2.15.1,
 * 2026-10-04). Il se reproduit sur l'éditeur monté seul, hors de Hector
 * (sonde N) : notre intégration n'y est pour rien.
 *
 * - **Ce qui le déclenche** (D0 à D11) : un plan `mutations.apply` que
 *   l'éditeur jette APRÈS avoir joué au moins une étape — étape refusée au
 *   rang 1 ou plus à l'exécution (`INVALID_CONTEXT`, ou `NO_OP`, dernière
 *   étape comprise : D12, D13, éditeur en mode édition). La révision a alors avancé du rang de l'étape refusée plus un,
 *   sans entrée d'historique. Ne le déclenchent pas : un refus à la première
 *   étape, un plan d'une étape refusé, un refus dès la compilation
 *   (`INVALID_TARGET`, `REVISION_MISMATCH` : la révision ne bouge pas), un
 *   aperçu. Le mode de l'éditeur n'y change rien ; un acte de trois paragraphes
 *   et un plan de deux étapes suffisent (D9).
 * - **Ce qui s'arrête** : le peintre. Dès le refus, plus aucun changement
 *   n'atteint la page (0 changement du DOM peint, contre 93 pour une écriture
 *   saine ; le compteur de peinture de l'éditeur, `paintHud`, n'enregistre plus
 *   de peinture — sonde N). Ne sont plus peints : une forme de texte ou un
 *   alignement posés par plan, un `format.apply` direct, un alignement hors
 *   plan (F1 à F4, F8) et **la frappe de l'avocat** (T1 : le texte tapé est au
 *   fichier, pas à l'écran). Le fichier exporté, lui, porte chaque écriture.
 * - **Attendre ne change rien** : rien n'est peint à 0,5 s, 2 s, 10 s, 30 s,
 *   60 s ni 120 s (A). Aucun geste d'interface non plus (G : défilement,
 *   redimensionnement, zoom, marques, mode, clic, sélection, export, signets,
 *   règle, contraste, focus, recherche, affichage des révisions).
 * - **Ce qui relance le peintre**, et peint d'un coup tout ce qui était en
 *   retard : un plan ACCEPTÉ dont deux étapes au moins ont un effet, de forme
 *   de texte (G21, G24) ou d'alignement (G26, G27) ; une écriture de paragraphe
 *   HORS PLAN de retrait, d'espacement, de bordure, de remise à zéro, de saut
 *   de page avant, de niveau de plan ou de style nommé (F9 à F11, G30 à G33,
 *   G35 à G47 — le détail, écriture par écriture, est dans l'en-tête de
 *   `peintreDeLEditeur.ts`) ; l'insertion d'un paragraphe en fin d'acte (F7) ;
 *   l'annulation d'une écriture d'alignement (G20). Ne le relancent pas : un plan accepté
 *   dont UNE seule étape a un effet (D2, F1 ; G29 : deux étapes d'alignement
 *   dont une sans effet), un plan d'alignement sans effet (G25), un plan refusé
 *   (G22, G23, G28, G34), un alignement hors plan (F8). La page est ensuite
 *   saine jusqu'au prochain refus de cette sorte (U2 à U4).
 * - **Aucun remède sans effet sur le document n'a été trouvé** (R1 à R4). Une
 *   écriture d'espacement aussitôt annulée relance le peintre et rend un
 *   fichier identique à l'octet, mais laisse un rétablissement en réserve
 *   (`redoDepth` 0 → 1) qui reposerait cet espacement, et `history.undo`
 *   déferait une frappe de l'avocat venue entre les deux. Il n'est pas installé.
 *
 * Ce que le code en fait : les étapes de texte que l'éditeur risque de refuser
 * — celles des paragraphes qui portent ce devant quoi il a refusé — sont
 * soumises SEULES, avant les lots (`isolerLesEtapes`, `repliDeLot.ts`) : leur
 * refus ne joue rien. Les lots qui restent n'ont plus de refus connu.
 *
 * L'état du peintre est celui du DOCUMENT monté, pas de l'appel : il vit sur
 * `CanvasDocumentScope` (`peintreEstArrete`, `noterLePeintre` ; remis à faux à
 * chaque instance d'éditeur neuve), et ses transitions sont décidées par
 * `peintreDeLEditeur.ts` sur ces seules mesures. Tant qu'il est arrêté, TOUT
 * reçu de forme le dit (`format.pageNotRepainted`,
 * `FORMAT_PAGE_NOT_REPAINTED`), y compris celui d'un appel sans refus. Les
 * verbes de CONTENU ne le lisent ni ne le relancent : après une insertion de
 * paragraphe par l'agent, il reste tenu pour arrêté jusqu'à une écriture de
 * forme qui le relance — un signal de trop, jamais un signal manqué.
 *
 * Ce qui reste ouvert : un lot refusé pour une cause que le fichier ne laisse
 * pas prévoir (ou un `NO_OP` que la prédiction a manqué) est rejoué par
 * moitiés, écrit et relu au fichier, et la page peut rester en retard — sur ce
 * qui est écrit comme sur ce que l'avocat tape — jusqu'à une écriture qui
 * relance le peintre. Ce défaut précède le repli : il vaut pour tout plan
 * refusé en cours de route.
 *
 * ## Un point d'extension
 *
 * **Une écriture par paragraphe** (`ecrireUnParagraphe`) prend l'appel à
 * l'éditeur en argument : un nouveau réglage sans étape de plan (#1867 :
 * bordure, encadré, remise à zéro) y passe tel quel, une fois par écriture que
 * l'éditeur demande.
 *
 * Un verbe qui écrit des paragraphes ENTIERS n'enchaîne pas ces appels
 * lui-même : une étape de plan par paragraphe, la série des écritures hors
 * plan et les comptes du reçu sont dans `travauxDeParagraphe.ts`.
 */

import { CANVAS_UNDO_HISTORY_DEPTH, type CanvasFormatPlace } from '@hector/shared-types';
import { abortedBeforeMutation, toCanvasSnapshotSummary, type CanvasActionReceipt } from './actionReceipt';
import { estObjet, lirePreview } from './apercuDePlan';
import { idsDeRevision } from './canvasSnapshot';
import { estArretDeLot } from './executionAsynchrone';
import { ExportDeLActeImpossible, exporterLActeEnMemoire } from './exportDeLActe';
import { lireLaRevisionDuDocument, marquerRevisionApresPlan, muterSousFences, REVISION_ABSENTE, type RevisionDeDocument } from './fencesDeMutation';
import { detailDuRefus } from './formatActionExecutors';
import { ecritureDirecteDansUnLieu, lieuDeLaPartie } from './horsDuCorps';
import { peintreArreteApres, peintreLocal, type PeintreDuDocument } from './peintreDeLEditeur';
import { corpsAvecSesCellules, LectureDeFormeImpossible, lireLaForme, type ActeVise, type FormeDeLActe, type MarqueDeParagraphe } from '@hector/docx-forme';
import type { EtapeDePlan } from './planDeFormatText';
import { causeBornee, type FaitsDeLEcritureDeForme, type RefusDeLEditeur } from './recuDEcritureDeForme';
import { avanceApresUnRefus, suiteDUnApercuInvalide, suiteDUnRefusALApplication, type EtapeRefusee } from './repliDeLot';
import { snapshot, type CanvasActionContext } from './textActionExecutors';
import type { RelectureDeLActe, RelectureDuFichier } from './verificationDeForme';

/**
 * Le fichier Word de l'acte, exporté et lu ; ou pourquoi il ne l'est pas. `cause` est le message de l'export ou du
 * lecteur, en anglais : il part tel quel dans le reçu que lit le modèle. `erreur` est ce qui a été levé — une erreur
 * que ni l'export ni le lecteur ne nomment reste à l'appelant, qui décide de la relancer.
 */
export type FichierDeLActeRelu = { acte: FormeDeLActe } | { cause: string; erreur: unknown };

/**
 * Exporte l'acte et lit sa forme, sans rien lever : LE passage par le fichier de tout verbe qui juge ce qu'il a écrit
 * (`relireLActeApresLEcriture` pour la forme, `set_page_layout`, `set_header_footer`). Un export par appel.
 */
export async function relireLeFichierDeLActe(doc: CanvasActionContext['doc']): Promise<FichierDeLActeRelu> {
  try {
    return { acte: await lireLaForme(await exporterLActeEnMemoire(doc)) };
  } catch (erreur) {
    return { cause: erreur instanceof Error ? erreur.message : String(erreur), erreur };
  }
}

/**
 * Relit le fichier Word de l'acte après une écriture, et ne le rend que s'il
 * est bien celui de cette écriture : la révision du document, relue après
 * l'export, doit être `cloture` (« Ce que la révision garde », en-tête). Sinon
 * la raison pour laquelle ce fichier ne juge rien — jamais une forme à croire.
 *
 * L'acte rendu est celui du lecteur : la manière d'écrire en tire ce que la cible visait (`ManiereDEcrire.relire`),
 * le reçu d'un plan le corps avec ses cellules (`corpsAvecSesCellules`).
 *
 * Partagée par tout ce qui juge une forme écrite : la clôture des verbes de
 * forme (`clore`) et le reçu d'un `apply_edit_plan` (`formeDuPlan.ts`, #1947).
 *
 * @param cloture La révision que l'écriture a laissée ; `undefined` : l'éditeur
 *   n'en a pas rendu, rien ne relie le fichier à l'écriture.
 */
export async function relireLActeApresLEcriture(ctx: CanvasActionContext, cloture: string | undefined): Promise<RelectureDuFichier> {
  if (cloture === undefined) return { nonRelu: 'revision_unavailable' };
  const relu = await relireLeFichierDeLActe(ctx.doc);
  if ('erreur' in relu) {
    if (!(relu.erreur instanceof ExportDeLActeImpossible || relu.erreur instanceof LectureDeFormeImpossible)) throw relu.erreur;
    return { nonRelu: 'reread_failed' };
  }
  const apresLaRelecture = await lireLaRevisionDuDocument(ctx.doc);
  if (apresLaRelecture === undefined) return { nonRelu: 'revision_unavailable' };
  return apresLaRelecture === cloture ? { acte: relu.acte } : { nonRelu: 'changed_during_reread' };
}

/** Le corps avec ses cellules, tiré du fichier relu : ce que juge une écriture dans le corps. */
export function corpsRelu(fichier: RelectureDuFichier): RelectureDeLActe {
  return 'acte' in fichier ? { acte: corpsAvecSesCellules(fichier.acte) } : fichier;
}

/**
 * Nombre maximal d'aperçus successifs d'un même plan. Le premier nomme toutes
 * les étapes que l'éditeur refuse ; le second porte le plan sans elles et doit
 * être valide. Valeur de jugement : un troisième tour est une marge, au-delà
 * l'appel est refusé plutôt que le plan rogné sans fin.
 */
const TOURS_D_APERCU_MAX = 3;

/** Ce que le socle fait d'un paragraphe dont la forme de paragraphe attend une décision (`w:pPrChange`). */
export type SortDUneRevisionDeFormeDeParagraphe = 'ecrire_et_nommer' | 'epargner';

/**
 * ⚠️ LA ligne de la décision — à confirmer par le porteur. `'epargner'` : ces
 * paragraphes ne sont plus écrits, ils sont sautés et nommés
 * (`format.skipped`, `pending_tracked_change`, dits épargnés).
 */
const SUR_UNE_REVISION_DE_FORME_DE_PARAGRAPHE = 'ecrire_et_nommer' as SortDUneRevisionDeFormeDeParagraphe;

/**
 * Partage les paragraphes d'une cible selon la seule règle des verbes de forme
 * pour un changement de forme de paragraphe en attente (`w:pPrChange`). Le
 * fait mesuré et la décision sont dans l'en-tête, « Un paragraphe dont la
 * forme attend une décision ».
 *
 * @param sort La décision à appliquer. Les verbes ne le passent pas : ils suivent la constante ; un test y passe les
 *   deux issues, pour que le retour à « épargner » reste sûr.
 * @returns `aEcrire` : les paragraphes que le verbe écrit ; `surRevision` :
 *   ceux d'entre eux qui portent la révision — le reçu nomme ceux qu'une
 *   écriture de forme de paragraphe a réellement changés ; `epargnes` : ceux qui ne sont pas écrits, à sauter et à nommer.
 */
export function ecrireOuEpargnerLesRevisionsDeFormeDeParagraphe<Paragraphe extends { porte: readonly MarqueDeParagraphe[] }>(
  paragraphes: readonly Paragraphe[],
  sort: SortDUneRevisionDeFormeDeParagraphe = SUR_UNE_REVISION_DE_FORME_DE_PARAGRAPHE,
): { aEcrire: Paragraphe[]; surRevision: Paragraphe[]; epargnes: Paragraphe[] } {
  const enAttente = paragraphes.filter((paragraphe) => paragraphe.porte.includes('revision_de_forme_de_paragraphe'));
  return sort === 'ecrire_et_nommer'
    ? { aEcrire: [...paragraphes], surRevision: enAttente, epargnes: [] }
    : { aEcrire: paragraphes.filter((paragraphe) => !enAttente.includes(paragraphe)), surRevision: [], epargnes: enAttente };
}

/** Un refus avant toute écriture : ce que l'agent lit, et s'il peut corriger l'appel et le renvoyer sans relire. */
export type Refus = { code: string; message: string; reessayer?: boolean };

/**
 * Le refus d'une écriture, tel que l'éditeur le dit : une erreur jetée (`NO_OP`,
 * `TARGET_CONFLICT`, `REVISION_MISMATCH`) ou un résultat rendu en échec. Le
 * code est gardé à part du détail : c'est sur lui que le reçu décide.
 */
function refusDeLEditeur(source: unknown): RefusDeLEditeur {
  if (source instanceof Error) {
    const code = estObjet(source) && typeof source.code === 'string' ? source.code : undefined;
    return { ...(code === undefined ? {} : { code }), detail: causeBornee(`${code === undefined ? '' : `${code}: `}${source.message}`) };
  }
  const echec = estObjet(source) && estObjet(source.failure) ? source.failure : undefined;
  return { ...(typeof echec?.code === 'string' ? { code: echec.code } : {}), detail: detailDuRefus(source) };
}

/**
 * Ce que l'aperçu d'un plan a rendu. `gardees` : les étapes que l'éditeur
 * accepte, dans leur ordre, avec `rendu`, la réponse de leur aperçu VALIDE
 * (absente si aucune étape ne reste). `refusees` : celles qu'il refuse, chacune
 * avec sa cause ; elles ne sont pas à écrire. `refus` : l'aperçu lui-même a
 * échoué (jeté, illisible, document changé, refus qui ne nomme aucune étape) —
 * rien n'en est déduit, l'appel est refusé avec sa cause.
 */
export type ApercuDUnPlan =
  | { rendu: unknown; gardees: EtapeDePlan[]; refusees: EtapeRefusee[] }
  | { refus: CanvasActionReceipt };

/**
 * Ce que l'application a fait des étapes d'un lot, par identifiant d'étape.
 * Une étape qui n'est dans aucune des trois suites n'a pas été soumise : une
 * interruption l'a précédée.
 */
export interface SortDUnLot {
  /** Les étapes appliquées. */
  ecrites: string[];
  /** Les étapes que l'éditeur dit sans effet : le texte est déjà dans l'état demandé. */
  sansObjet: string[];
  /** Les étapes que l'éditeur refuse, chacune avec sa cause : sautées, le reste du lot est écrit. */
  sautees: EtapeRefusee[];
}

/**
 * L'issue d'une écriture par paragraphe. `sans_objet` : l'éditeur dit qu'elle
 * ne changerait rien (`NO_OP`). `refusee` : elle interrompt la série — toute
 * écriture demandée ensuite est refusée sans être soumise.
 */
export type IssueDUneEcriture = 'ecrite' | 'sans_objet' | 'refusee';

/** Ce que la clôture rend au reçu : les faits de l'écriture qui ne dépendent pas du verbe. */
export type ClotureDeLEcriture = Pick<
  FaitsDeLEcritureDeForme,
  'ecritures' | 'ecrituresNonAnnulables' | 'historiqueInexplique' | 'interruption' | 'pageNonRepeinte' | 'avant' | 'apres' | 'acteAvant' | 'relecture' | 'revisions' | 'lieu'
>;

/**
 * Les verbes qui écrivent par le socle : ceux de forme, et `rewrite_block`, qui
 * écrit le texte de paragraphes puis la forme de ce texte (en-tête, « Un plan de texte avant la forme »).
 */
export type VerbeDuSocle = FaitsDeLEcritureDeForme['verbe'] | 'rewrite_block';

/** Ce que l'exécuteur dit au socle de ses plans. */
export interface OptionsDesPlans {
  /**
   * Le régime de ces plans. Absent : direct, la règle de toute écriture de forme. `tracked` n'a qu'un emploi, le plan
   * de TEXTE de `rewrite_block` sous suivi activé : le texte suit le régime du document, sa forme jamais.
   */
  changeMode?: 'tracked' | 'direct';
  /** Vrai si des écritures par paragraphe suivent le dernier plan : un dernier plan appliqué sans révision rendue interrompt alors la suite. */
  dAutresEcrituresSuivent?: boolean;
  /**
   * Appelé après chaque plan refusé, avant tout rejeu, avec les étapes qui
   * restent à soumettre : l'exécuteur y re-prouve celles qui s'adressent par
   * POSITION dans le texte. Le refus a fait avancer la révision, et une frappe
   * de l'avocat venue pendant ce temps ne s'en distingue pas toujours
   * (`avanceApresUnRefus`, `repliDeLot.ts`) : une position prouvée avant le
   * refus ne l'est plus. Rend la cause qui interrompt la série, ou `undefined`
   * si tout est prouvé. Absent : aucune étape ne s'adresse par position.
   */
  reprouver?: (restantes: readonly EtapeDePlan[]) => Promise<RefusDeLEditeur | undefined>;
}

/**
 * L'état d'une écriture ouverte : ce que la manière d'écrire et les écritures
 * par paragraphe tiennent à jour, et que la clôture lit.
 */
export interface SuiviDeLEcriture {
  /** La révision que la prochaine écriture attend ; celle de l'ouverture tant que rien n'est écrit. */
  attendue: RevisionDeDocument;
  /** La révision que la dernière écriture appliquée a laissée ; `undefined` : l'éditeur ne l'a pas rendue. */
  apresLEcriture: RevisionDeDocument | undefined;
  /** Écritures appliquées : autant d'entrées de l'historique de l'éditeur (en-tête, « Ce qui compte comme une écriture »). */
  ecritures: number;
  /** Le refus qui a interrompu la série ; plus rien n'est soumis ensuite. */
  interruption: RefusDeLEditeur | undefined;
  /** Vrai dès qu'une écriture a été soumise à l'éditeur : il y a alors quelque chose à relire. */
  tentee: boolean;
  /** Vrai si la dernière écriture est hors plan : la révision qu'elle laisse est à relire. */
  horsPlan: boolean;
}

/** Ce que le socle prête à une manière d'écrire. */
export interface OutilsDuSocle {
  ctx: CanvasActionContext;
  verbe: VerbeDuSocle;
  /** Le reçu d'un refus avant toute écriture. */
  refuser(refus: Refus): CanvasActionReceipt;
  /** La révision du document, lue à l'instant ; `undefined` : l'éditeur n'en rend pas. */
  revisionLue(): Promise<RevisionDeDocument | undefined>;
  /** Le refus d'une écriture tel que l'éditeur le dit, jeté ou rendu. */
  refusDeLEditeur(source: unknown): RefusDeLEditeur;
  suivi: SuiviDeLEcriture;
}

/**
 * Une MANIÈRE d'écrire les étapes d'un appel, choisie une fois à l'ouverture
 * d'après le lieu de la cible. Il y en a deux : par PLAN atomique dans le corps
 * ({@link ecritureParPlans}), DIRECTE dans un en-tête, un pied ou une note, où
 * l'éditeur refuse les plans (`ecritureDirecteDansUnLieu`, `horsDuCorps.ts`).
 * Tout ce qui dépend d'où l'on écrit est derrière cette interface ; le socle et
 * les exécuteurs ne retestent le lieu nulle part.
 */
export interface ManiereDEcrire {
  /** Les surfaces de l'éditeur que cette manière emploie, par leur chemin sous `editor.doc`. */
  surfaces: Readonly<Record<string, unknown>>;
  /**
   * Les paragraphes où la cible se résout, tirés du fichier lu : le corps avec
   * ses cellules de tableau, ceux du lieu sinon — avec ce que conseille un refus quand la
   * cible n'y désigne rien ({@link EcritureDeFormeOuverte.siLaCibleEstVide}).
   * `absent` : la phrase qui dit que le document ne porte pas ce lieu.
   */
  viser(entier: FormeDeLActe): Promise<{ acte: ActeVise; siLaCibleEstVide?: string } | { absent: string }>;
  /** Le contrat d'{@link EcritureDeFormeOuverte.apercevoir}. */
  apercevoir(steps: readonly EtapeDePlan[], nomDuPlan: string): Promise<ApercuDUnPlan>;
  /** Le contrat d'{@link EcritureDeFormeOuverte.appliquerLesPlans}. */
  appliquer(lots: ReadonlyArray<readonly EtapeDePlan[]>, etiquette: string, options?: OptionsDesPlans): Promise<SortDUnLot[]>;
  /** Le contrat de `viser`, sur le fichier relu APRÈS l'écriture : ce que la cible visait, ou la raison pour laquelle il ne juge rien. */
  relire(relecture: RelectureDuFichier): Promise<RelectureDeLActe>;
  /** Ce que cette manière ajoute aux faits du reçu. */
  faits: Pick<ClotureDeLEcriture, 'lieu'>;
}

/** Une écriture de forme ouverte : le fichier de l'acte est lu, et rien n'est encore écrit. */
export interface EcritureDeFormeOuverte {
  /** Ce que la cible vise dans le fichier exporté, clôturé sur la révision du document : elle se résout sur lui. */
  acte: ActeVise;
  /**
   * Ce qu'un refus « la cible ne désigne aucun paragraphe à écrire » conseille
   * quand elle vise un lieu : le lire, et les lieux que le document porte.
   * Absent dans le corps, où chaque verbe dit son propre conseil.
   */
  siLaCibleEstVide?: string;
  /**
   * L'en-tête, le pied ou la note où le fichier porte ce paragraphe, quand la cible vise le corps : un identifiant que
   * le corps ne porte pas n'est pas pour autant inconnu, et le refus peut nommer le verbe qui l'écrit. `undefined` :
   * le fichier ne le porte dans aucun de ces lieux.
   */
  lieuHorsDuCorpsDe(nodeId: string): CanvasFormatPlace | undefined;
  /** Le reçu d'un refus avant toute écriture. */
  refuser(refus: Refus): CanvasActionReceipt;
  /**
   * Soumet un plan à l'aperçu, sous la révision que la prochaine écriture
   * attend — celle de l'ouverture tant que rien n'est écrit —, et en retire les
   * étapes que l'éditeur refuse jusqu'à ce que l'aperçu soit valide. Rien n'est écrit.
   *
   * @param nomDuPlan Ce que le plan écrit, pour le message d'un aperçu illisible (« formatting », « alignment »).
   */
  apercevoir(steps: readonly EtapeDePlan[], nomDuPlan: string): Promise<ApercuDUnPlan>;
  /**
   * Applique les plans dans l'ordre, chacun atomique et direct, chacun sous la
   * révision que le précédent a rendue. Un plan que l'éditeur refuse est rejoué
   * par moitiés jusqu'à l'étape qu'il refuse, qui est sautée ; un refus qui
   * porte sur le document interrompt tout (`repliDeLot.ts`).
   *
   * @param etiquette Nom de l'étape dans les traces (`plan`, `alignement`).
   * @returns Le sort des étapes de chaque lot, dans l'ordre des lots.
   */
  appliquerLesPlans(lots: ReadonlyArray<readonly EtapeDePlan[]>, etiquette: string, options?: OptionsDesPlans): Promise<SortDUnLot[]>;
  /**
   * Soumet UNE écriture hors plan, gardée par la révision lue juste avant.
   *
   * @param etiquette Nom de l'écriture dans les traces (`retraits:<nodeId>`).
   * @param appel L'appel à l'éditeur — UN seul appel fournisseur : un refus passager rendu par l'éditeur est rejoué tel quel.
   * @param relanceLePeintre Vrai si cette écriture, appliquée, relance le peintre de l'éditeur arrêté par un plan refusé
   *   — la table des écritures mesurées est `RELANCE_LE_PEINTRE` (`peintreDeLEditeur.ts`). Faux pour toute écriture non mesurée.
   */
  ecrireUnParagraphe(
    etiquette: string,
    appel: (options: { changeMode: 'direct'; expectedRevision: RevisionDeDocument }) => unknown,
    relanceLePeintre?: boolean,
  ): Promise<IssueDUneEcriture>;
  /** Relit le fichier après l'écriture et rend les faits communs du reçu. À appeler une fois, en dernier. */
  clore(): Promise<ClotureDeLEcriture>;
}

/**
 * La manière d'écrire du CORPS : des plans atomiques, aperçus puis appliqués
 * sous la révision que le précédent a rendue, rejoués par moitiés quand
 * l'éditeur en refuse un (en-tête, « Quand l'éditeur refuse un lot »).
 *
 * @param peintre L'état du peintre de l'éditeur, que seul un plan arrête.
 */
function ecritureParPlans({ ctx, verbe, refuser, revisionLue, suivi }: OutilsDuSocle, peintre: PeintreDuDocument): ManiereDEcrire {
  // Les méthodes sont appelées SUR leur surface, jamais détachées : rien ne
  // garantit qu'une méthode du fournisseur reste liée une fois extraite.
  const plans = ctx.doc.mutations;
  const planDe = (steps: readonly EtapeDePlan[], expectedRevision: string, changeMode: 'tracked' | 'direct' = 'direct') => ({ atomic: true as const, changeMode, expectedRevision, steps });
  return {
    surfaces: { 'mutations.preview': plans?.preview, 'mutations.apply': plans?.apply },

    viser: async (entier) => ({ acte: corpsAvecSesCellules(entier) }),

    async apercevoir(steps, nomDuPlan) {
      let gardees = [...steps];
      const refusees: EtapeRefusee[] = [];
      for (let tour = 1; gardees.length > 0; tour += 1) {
        let rendu: unknown;
        try {
          rendu = await plans?.preview?.(planDe(gardees, suivi.attendue));
        } catch (erreur) {
          if (estArretDeLot(erreur)) throw erreur;
          return {
            refus: refuser({
              code: 'FORMAT_WRITE_REFUSED',
              message: `${verbe} was refused by the editor before any write (${refusDeLEditeur(erreur).detail}). Nothing was written.`,
            }),
          };
        }
        const apercu = lirePreview(rendu);
        if (apercu === null) {
          return {
            refus: refuser({
              code: 'FORMAT_PLAN_PREVIEW_UNREADABLE',
              message: `${verbe} was refused before any write: the editor returned an unreadable preview of the ${nomDuPlan} plan, `
                + 'so the write could not be validated. Nothing was written.',
            }),
          };
        }
        if (apercu.valid) return { rendu, gardees, refusees };
        // `REVISION_MISMATCH` a deux sens à l'aperçu (voir l'en-tête) : seule la révision relue dit si le document a bougé.
        if (apercu.failures?.some((echec) => echec.code === 'REVISION_MISMATCH') === true && (await revisionLue()) !== suivi.attendue) {
          return {
            refus: refuser({
              code: 'STALE_CANVAS_REVISION',
              reessayer: true,
              message: `${verbe} was refused before any write: the document changed while the ${nomDuPlan} plan was being validated `
                + '(the lawyer is typing, or another write is in flight). Nothing was written; resend the same action.',
            }),
          };
        }
        const suite = suiteDUnApercuInvalide(gardees, apercu.failures);
        if (suite.issue === 'refuser' || tour === TOURS_D_APERCU_MAX) {
          const cause = suite.issue === 'refuser' ? suite.cause : suite.refusees[0]?.refus.detail ?? 'the editor named no cause';
          return {
            refus: refuser({
              code: 'FORMAT_WRITE_REFUSED',
              message: `${verbe} was refused by the editor before any write: its preview of the ${nomDuPlan} plan is invalid `
                + `${suite.issue === 'refuser' ? 'and names no step of the plan to leave out' : `after ${TOURS_D_APERCU_MAX} previews, each leaving out the steps the editor refused`} `
                + `(${causeBornee(cause)}). Nothing was written. Resending the same action will be refused the same way; `
                + 'read the form of the target (formatOf) and narrow the target.',
            }),
          };
        }
        refusees.push(...suite.refusees);
        ({ gardees } = suite);
      }
      return { rendu: undefined, gardees, refusees };
    },

    async appliquer(lots, etiquette, { dAutresEcrituresSuivent = false, reprouver, changeMode = 'direct' } = {}) {
      const sorts = lots.map((): SortDUnLot => ({ ecrites: [], sansObjet: [], sautees: [] }));
      // Les plans à soumettre, dans l'ordre du document ; un plan refusé y remet ses deux moitiés en tête.
      const file = lots.flatMap((steps, lot) => (steps.length === 0 ? [] : [{ lot, steps }]));
      // La file se vide : un plan appliqué, sauté ou sans objet en sort ; un plan refusé de n étapes y remet deux
      // moitiés strictement plus courtes. Un lot de n étapes donne donc au plus 2n − 1 plans.
      const plansMax = file.reduce((somme, plan) => somme + 2 * plan.steps.length - 1, 0);
      for (let soumis = 1; file.length > 0; soumis += 1) {
        const plan = file.shift();
        const sort = plan === undefined ? undefined : sorts[plan.lot];
        if (plan === undefined || sort === undefined || soumis > plansMax) {
          throw new Error(
            `${verbe} (${etiquette}): the replay of refused batches did not end — plan ${soumis} submitted for ${lots.length} batch(es) `
            + `that allow at most ${plansMax} plans. This is a defect of ecritureDeForme.ecritureParPlans; report it with this message.`,
          );
        }
        suivi.tentee = true;
        suivi.horsPlan = false;
        const { attendue } = suivi;
        let resultat: unknown;
        let refus: RefusDeLEditeur | undefined;
        try {
          // UN appel fournisseur par fermeture : un refus passager RENDU par
          // l'éditeur (export en cours) est alors rejoué sans risque de doublon.
          resultat = await muterSousFences(ctx, `${verbe}:${etiquette}-${soumis}`, { changeMode, expectedRevision: attendue }, () => plans?.apply?.(planDe(plan.steps, attendue, changeMode)));
        } catch (erreur) {
          if (estArretDeLot(erreur)) throw erreur;
          refus = refusDeLEditeur(erreur);
        }
        if (refus === undefined && (!estObjet(resultat) || resultat.success !== true)) refus = refusDeLEditeur(resultat);

        if (refus === undefined) {
          sort.ecrites.push(...plan.steps.map((step) => step.id));
          suivi.apresLEcriture = marquerRevisionApresPlan(resultat);
          // Un plan qui n'a rien changé rend la révision qu'il a reçue et ne laisse aucune entrée d'historique :
          // il n'est pas compté, sinon l'annulation de `writes` écritures déferait une action antérieure de l'avocat.
          if (suivi.apresLEcriture !== attendue) suivi.ecritures += 1;
          if (suivi.apresLEcriture === undefined) {
            // Sans la révision d'après, l'écriture suivante n'a plus de verrou : elle n'est pas soumise.
            if (file.length > 0 || dAutresEcrituresSuivent) {
              suivi.interruption = { detail: 'the editor applied a write without reporting the document revision after it, so the next write could not be guarded' };
            }
            break;
          }
          // Le peintre arrêté ne repart que si deux étapes au moins ont eu un effet : l'avance de la révision le dit.
          peintre.noter(peintreArreteApres(peintre.estArrete(), { geste: 'plan_applique', avant: attendue, apres: suivi.apresLEcriture }));
          suivi.attendue = suivi.apresLEcriture;
          continue;
        }

        const suite = suiteDUnRefusALApplication(plan.steps, refus);
        if (suite.issue === 'arreter') {
          suivi.interruption = refus;
          break;
        }
        // Un plan refusé n'écrit rien mais peut faire avancer la révision (voir l'en-tête) : elle est relue.
        suivi.apresLEcriture = await revisionLue();
        if (suivi.apresLEcriture === undefined) {
          suivi.interruption = { detail: 'the editor reported no document revision (editor.doc.info) after refusing a write, so the next write could not be guarded' };
          break;
        }
        const avance = avanceApresUnRefus(attendue, suivi.apresLEcriture, plan.steps.length);
        if (avance === 'document_change') {
          // La révision a bougé sans qu'on sache de combien le refus y est : des étapes ont pu être jouées.
          if (plan.steps.length >= 2) peintre.noter(true);
          suivi.interruption = {
            code: 'STALE_CANVAS_REVISION',
            detail: `STALE_CANVAS_REVISION: while the editor was refusing a batch of ${plan.steps.length} step(s), the document revision went from ${attendue} to ${suivi.apresLEcriture}, `
              + 'which that refusal alone does not explain (it leaves the revision unchanged, or moves it by 2 up to the number of steps): '
              + 'the lawyer typed, or another write ran, during the call. The remaining writes were not submitted',
          };
          break;
        }
        peintre.noter(peintreArreteApres(peintre.estArrete(), { geste: 'plan_refuse', avance }));
        suivi.attendue = suivi.apresLEcriture;
        if (suite.issue === 'redecouper') file.unshift(...suite.moities.map((steps) => ({ lot: plan.lot, steps })));
        else if (suite.issue === 'sans_objet') sort.sansObjet.push(suite.stepId);
        else sort.sautees.push({ stepId: suite.stepId, refus });
        // Des étapes jouées puis défaites : la révision a bougé, et ce qui reste à soumettre a été prouvé AVANT. Les
        // étapes par position le sont de nouveau. Sans étape jouée, la révision est celle d'avant : rien n'a changé.
        const nonProuve = avance !== 'etapes_jouees' || file.length === 0 ? undefined : await reprouver?.(file.flatMap((reste) => reste.steps));
        if (nonProuve !== undefined) {
          suivi.interruption = nonProuve;
          break;
        }
      }
      return sorts;
    },

    relire: async (fichier) => corpsRelu(fichier),
    faits: {},
  };
}

/**
 * Ouvre une écriture de forme : vérifie que l'éditeur expose de quoi écrire,
 * que le document a une révision et qu'elle est celle que l'agent a lue, puis
 * exporte et lit le fichier de l'acte, clôturé sur cette révision.
 *
 * @param autresSurfaces Les surfaces de l'éditeur que ce verbe emploie en plus de celles de sa manière d'écrire, par leur chemin sous `editor.doc`.
 * @param lieu L'en-tête, le pied ou les notes que la cible vise ; absent : le corps. Il décide de la manière d'écrire, ici et nulle part ailleurs.
 * @returns L'écriture ouverte, ou le reçu du refus (`aborted`, cause nommée, rien d'écrit).
 */
export async function ouvrirLEcritureDeForme(
  ctx: CanvasActionContext,
  verbe: VerbeDuSocle,
  autresSurfaces: Readonly<Record<string, unknown>> = {},
  lieu?: CanvasFormatPlace,
): Promise<EcritureDeFormeOuverte | { refus: CanvasActionReceipt }> {
  const pre = await snapshot(ctx, { offset: 0, limit: 1 });
  const avant = toCanvasSnapshotSummary(pre);
  const refuser = (refus: Refus): CanvasActionReceipt => abortedBeforeMutation({
    intent: verbe,
    snapshot: avant,
    code: refus.code,
    message: refus.message,
    ...(refus.reessayer === true
      ? { recovery: { action: 'retry' as const, reason: 'Refused before any write: nothing was written. Correct the call as the message says and resend it, without re-reading.' } }
      : {}),
  });
  // Lue à l'instant sur la seule surface que le verrou accepte ; `undefined` : l'éditeur n'en rend pas.
  const revisionLue = async (): Promise<RevisionDeDocument | undefined> => lireLaRevisionDuDocument(ctx.doc);
  const suivi: SuiviDeLEcriture = { attendue: pre.revision, apresLEcriture: undefined, ecritures: 0, interruption: undefined, tentee: false, horsPlan: false };
  // L'état du peintre de l'éditeur est celui du DOCUMENT monté : arrêté par un appel précédent, il l'est encore
  // (en-tête, « La page après un plan refusé »). Un contexte qui n'en porte pas en tient un le temps de l'appel.
  const peintre = ctx.peintre ?? peintreLocal();
  const outils: OutilsDuSocle = { ctx, verbe, refuser, revisionLue, refusDeLEditeur, suivi };
  // ⚠️ Le SEUL aiguillage sur le lieu : tout ce qui en dépend est derrière la manière d'écrire.
  const maniere = lieu === undefined ? ecritureParPlans(outils, peintre) : ecritureDirecteDansUnLieu(outils, lieu);

  const manquantes = Object.entries({ ...maniere.surfaces, ...autresSurfaces })
    .filter(([, surface]) => typeof surface !== 'function')
    .map(([chemin]) => `editor.doc.${chemin}`);
  if (manquantes.length > 0) {
    return {
      refus: refuser({
        code: 'CAPABILITY_UNAVAILABLE',
        message: `${verbe} requires ${manquantes.join(', ')}, which this editor does not expose. `
          + 'Nothing was written, and resending the same action will fail the same way.',
      }),
    };
  }
  // Sans révision, aucune des deux clôtures ne clôt rien : comparer la
  // sentinelle d'absence à elle-même passerait toujours.
  const sansRevision = (moment: string): { refus: CanvasActionReceipt } => ({
    refus: refuser({
      code: 'FORMAT_REVISION_UNAVAILABLE',
      message: `${verbe} was refused before any write: the editor reported no document revision (editor.doc.info) ${moment}, `
        + 'so nothing ties the target to the document state it would be written on. Nothing was written. '
        + 'Run canvas_inspect once; if it reports no revision either, formatting is unavailable on this document.',
    }),
  });
  if (pre.revision === REVISION_ABSENTE) return sansRevision('at the start of the action');
  if (ctx.expectedRevision !== undefined && ctx.expectedRevision !== pre.revision) {
    return {
      refus: refuser({
        code: 'STALE_CANVAS_REVISION',
        message: `${verbe} was refused before any write: the document revision (${pre.revision}) is no longer the inspected one `
          + `(${ctx.expectedRevision}). Nothing was written; run canvas_inspect and resend the action.`,
      }),
    };
  }

  let vise: Awaited<ReturnType<ManiereDEcrire['viser']>>;
  let horsDuCorps: FormeDeLActe['horsDuCorps'] = [];
  try {
    const entier = await lireLaForme(await exporterLActeEnMemoire(ctx.doc));
    ({ horsDuCorps } = entier);
    vise = await maniere.viser(entier);
  } catch (erreur) {
    if (!(erreur instanceof ExportDeLActeImpossible || erreur instanceof LectureDeFormeImpossible)) throw erreur;
    return {
      refus: refuser({
        code: 'FORMAT_READ_UNAVAILABLE',
        message: `${verbe} was refused before any write because its target could not be resolved against the document file: ${erreur.message}`,
      }),
    };
  }
  if ('absent' in vise) {
    return {
      refus: refuser({
        code: 'FORMAT_PLACE_NOT_FOUND',
        message: `${verbe} was refused before any write: ${vise.absent} Nothing was written. `
          + 'Target a place this document has, or its body (no "in"). A header or a footer that does not exist yet is created by set_header_footer.',
      }),
    };
  }
  const { acte: acteAvant, siLaCibleEstVide } = vise;
  ctx.verifierEpoque(`${verbe}:apres-lecture-de-la-cible`);
  // La cible est résolue sur le fichier exporté : si le document a changé
  // pendant l'export, elle ne décrit plus la révision qu'on s'apprête à écrire.
  const revisionApresLaCible = await revisionLue();
  if (revisionApresLaCible === undefined) return sansRevision('after the target was read from the document file');
  if (revisionApresLaCible !== pre.revision) {
    return {
      refus: refuser({
        code: 'STALE_CANVAS_REVISION',
        reessayer: true,
        message: `${verbe} was refused before any write: the document changed while its target was being resolved `
          + '(the lawyer is typing, or another write is in flight). Nothing was written; resend the same action.',
      }),
    };
  }
  const revisionsAvant = new Set(idsDeRevision(pre.trackedChanges));
  // La profondeur de l'historique de l'éditeur ; `undefined` : il ne la dit pas.
  // Une lecture qui rejette vaut « il ne la dit pas » : un défaut de diagnostic ne coûte pas le reçu d'une écriture appliquée.
  const profondeurDHistorique = async (): Promise<number | undefined> => {
    try {
      const lu = await ctx.doc.history?.get?.();
      return estObjet(lu) && typeof lu.undoDepth === 'number' ? lu.undoDepth : undefined;
    } catch {
      return undefined;
    }
  };
  const historiqueAvant = await profondeurDHistorique();

  return {
    acte: acteAvant,
    ...(siLaCibleEstVide === undefined ? {} : { siLaCibleEstVide }),
    lieuHorsDuCorpsDe(nodeId) {
      const porteur = horsDuCorps.find(({ paragraphes, paragraphesDeTableau }) => [...paragraphes, ...paragraphesDeTableau].some((lu) => lu.nodeId === nodeId));
      return porteur === undefined ? undefined : lieuDeLaPartie(porteur.partie);
    },
    refuser,
    apercevoir: (steps, nomDuPlan) => maniere.apercevoir(steps, nomDuPlan),
    appliquerLesPlans: (lots, etiquette, options) => maniere.appliquer(lots, etiquette, options),

    async ecrireUnParagraphe(etiquette, appel, relanceLePeintre = false) {
      if (suivi.interruption !== undefined) return 'refusee';
      suivi.tentee = true;
      suivi.horsPlan = true;
      const revision = await revisionLue();
      if (revision === undefined) {
        suivi.interruption = { detail: 'the editor reported no document revision (editor.doc.info) before a write, so that write could not be guarded' };
        return 'refusee';
      }
      const options = { changeMode: 'direct' as const, expectedRevision: revision };
      let resultat: unknown;
      try {
        resultat = await muterSousFences(ctx, `${verbe}:${etiquette}`, options, () => appel(options));
      } catch (erreur) {
        if (estArretDeLot(erreur)) throw erreur;
        suivi.interruption = refusDeLEditeur(erreur);
        return 'refusee';
      }
      if (estObjet(resultat) && resultat.success === true) {
        suivi.ecritures += 1;
        if (relanceLePeintre) peintre.noter(peintreArreteApres(peintre.estArrete(), { geste: 'ecriture_hors_plan_qui_relance' }));
        return 'ecrite';
      }
      const refus = refusDeLEditeur(resultat);
      // « Ne changerait rien » n'est pas un échec : le paragraphe est dans l'état demandé, et la relecture le juge.
      if (refus.code === 'NO_OP') return 'sans_objet';
      suivi.interruption = refus;
      return 'refusee';
    },

    async clore() {
      if (suivi.horsPlan && suivi.interruption === undefined) suivi.apresLEcriture = await revisionLue();
      const post = await snapshot(ctx, { offset: 0, limit: 1 });
      const cloture = suivi.interruption === undefined ? suivi.apresLEcriture : post.revision === REVISION_ABSENTE ? undefined : post.revision;
      // Sans écriture tentée, l'acte lu à l'ouverture est déjà cette relecture.
      const relecture: RelectureDeLActe = suivi.tentee ? await maniere.relire(await relireLActeApresLEcriture(ctx, cloture)) : { acte: acteAvant };
      const revisionsApres = idsDeRevision(post.trackedChanges);
      const historiqueApres = await profondeurDHistorique();
      ctx.verifierEpoque(`${verbe}:receipt`);
      const { ecritures, interruption } = suivi;
      // Ce que l'historique a gagné pendant l'appel, quand ce n'est pas ce que les écritures comptées expliquent : une
      // entrée chacune, jusqu'à la profondeur qu'il garde. L'avocat a tapé ou annulé, ou une autre écriture est passée, entre deux.
      const inexpliquees = historiqueAvant !== undefined && historiqueApres !== undefined
        && historiqueApres !== Math.min(CANVAS_UNDO_HISTORY_DEPTH, historiqueAvant + ecritures);
      return {
        ecritures,
        // L'historique de l'éditeur ne garde que ses dernières entrées : les premières écritures d'un long appel ne se défont plus.
        ...(ecritures > CANVAS_UNDO_HISTORY_DEPTH ? { ecrituresNonAnnulables: ecritures - CANVAS_UNDO_HISTORY_DEPTH } : {}),
        ...(inexpliquees ? { historiqueInexplique: { avant: historiqueAvant, apres: historiqueApres } } : {}),
        ...(interruption === undefined ? {} : { interruption }),
        ...(peintre.estArrete() ? { pageNonRepeinte: true as const } : {}),
        ...maniere.faits,
        avant,
        apres: toCanvasSnapshotSummary(post),
        acteAvant,
        relecture,
        revisions: {
          nouvelles: revisionsApres.filter((id) => !revisionsAvant.has(id)),
          disparues: [...revisionsAvant].filter((id) => !revisionsApres.includes(id)),
        },
      };
    },
  };
}
